'use client';

import type { Address } from '@ahm/api-wrappers-core';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useAuth } from '@/providers/auth-provider';
import { useAiFoodOrderStore } from '@/store';

interface StoreCitySheetContextType {
  showCityPopup: boolean;
  isNavigating: boolean;
  openCitySheet: () => void;
  closeCitySheet: () => void;
  setNavigating: (navigating: boolean) => void;
  address: Address | null;
  setAddress: (address: Address | null) => void;
}

const StoreCitySheetContext = createContext<
  StoreCitySheetContextType | undefined
>(undefined);

export function StoreCitySheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Get loading state from auth provider to check if URL params are being processed
  const { isLoading } = useAuth();

  // Use Zustand store instead of local state
  const storeAddress = useAiFoodOrderStore.use.address();
  const setStoreAddress = useAiFoodOrderStore.use.setAddress();
  const requireAddressSync = useAiFoodOrderStore.use.requireAddressSync();
  const setRequireAddressSync = useAiFoodOrderStore.use.setRequireAddressSync();

  // Delay initial check to avoid triggering on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000); // Wait 1 second after mount

    return () => clearTimeout(timer);
  }, []);

  // City-mismatch machinery removed: the app flow no longer carries the city
  // in the URL, so "URL city ≠ address city" can't happen. The sheet now only
  // opens manually, via requireAddressSync, or when no address exists.

  // Auto-check for requireAddressSync flag and open sheet if needed
  useEffect(() => {
    if (
      !isInitialLoad &&
      !isNavigating &&
      !showCityPopup && // Don't open if sheet is already open
      !isLoading && // Don't open sheet while auth-provider is loading
      requireAddressSync
    ) {
      console.log('🔄 Opening city sheet due to requireAddressSync flag');
      setShowCityPopup(true);
      // Reset the flag once we've acted on it
      setRequireAddressSync(false);
    }
  }, [
    requireAddressSync,
    isInitialLoad,
    isNavigating,
    showCityPopup,
    isLoading, // Add isLoading as dependency
    setRequireAddressSync,
  ]);

  const openCitySheet = useCallback(() => {
    // Don't open sheet if auth-provider is loading (processing URL params, login, etc.)
    if (!isNavigating && !isLoading) {
      console.log('🏠 Opening city sheet manually');
      setShowCityPopup(true);
    } else if (isLoading) {
      console.log('🏠 Skipping city sheet open - auth provider is loading');
    }
  }, [isNavigating, isLoading]);

  const closeCitySheet = useCallback(() => {
    setShowCityPopup(false);
  }, []);

  const setNavigating = useCallback((navigating: boolean) => {
    setIsNavigating(navigating);
    if (navigating) {
      setShowCityPopup(false); // Close sheet when navigating
    }
  }, []);

  const value = {
    showCityPopup,
    isNavigating,
    openCitySheet,
    closeCitySheet,
    setNavigating,
    address: storeAddress,
    setAddress: setStoreAddress,
  };

  return (
    <StoreCitySheetContext.Provider value={value}>
      {children}
    </StoreCitySheetContext.Provider>
  );
}

export function useStoreCitySheet() {
  const context = useContext(StoreCitySheetContext);
  if (context === undefined) {
    throw new Error(
      'useStoreCitySheet must be used within a StoreCitySheetProvider'
    );
  }
  return context;
}
